<script lang="ts">
  /**
   * The category view, beside the category search on filter-bar line 2: one "View" select that
   * names the set of categories on screen, the way a saved search does. It replaces the
   * "Org default active" pill and the "Back to org default", "Show all" and "Save as org default"
   * buttons (spec 6.2 and 6.3 as amended by #2244); the selected category chips are still line 3,
   * `ActiveCategoryChips`, placed by the container.
   *
   * Every decision is already made in the view model - this component only reads
   * `InfoTypeFilterControls` and shouts:
   *
   *  - **Org default** is offered while a saved non-empty default exists; picking it drops
   *    `?infoTypes` again.
   *  - **All categories** clears the category filter. It is never the org default.
   *  - **Custom (unsaved)** is not a choice: it only appears, ticked, while the selection differs
   *    from both, so the trigger always says what is on screen.
   *  - **Save current as org default** is offered to `org:write` only, when the selection is
   *    non-empty and differs from the default. It waits for the last batch (`saveDisabled`, #2265):
   *    the save expands Uncategorised over every row.
   *
   * Until the org's saved default has been read (`controls.settled`) the select is disabled and reads
   * "Loading…" rather than naming a view that flips under the reader when the GET lands.
   *
   * The save is an action, not a view, so the select is re-keyed after every pick: bits-ui would
   * otherwise keep "Save" as its own remembered pick if the save failed and the view stayed put.
   */
  import * as Select from "$ui/select";

  import type { InfoTypeFilterControls } from "./activeInfoTypes";

  let {
    controls,
    canWrite = false,
    saveDisabled = false,
    onShowAll,
    onBackToOrgDefault,
    onSaveAsOrgDefault,
  }: {
    controls: InfoTypeFilterControls;
    /** `org:write`. The save is absent, not disabled, without it. */
    canWrite?: boolean;
    /** The rows are still loading; the save waits for the last batch. */
    saveDisabled?: boolean;
    /** Called when "All categories" is picked: clear the category filter. */
    onShowAll: () => void;
    /** Called when "Org default" is picked: follow the org default again. */
    onBackToOrgDefault: () => void;
    /** Called when "Save current as org default" is picked. */
    onSaveAsOrgDefault: () => void;
  } = $props();

  type View = "org-default" | "show-all" | "custom";
  const SAVE = "save";
  const VIEW_LABEL: Record<View, string> = {
    "org-default": "Org default",
    "show-all": "All categories",
    custom: "Custom (unsaved)",
  };

  const hasDefault = $derived(controls.isOrgDefault || controls.canReturnToDefault);
  const view = $derived<View>(controls.isOrgDefault ? "org-default" : controls.showAll ? "show-all" : "custom");
  const canSave = $derived(canWrite && controls.canSave);
  const LOADING_LABEL = "Loading…";
  const shownLabel = $derived(controls.settled ? VIEW_LABEL[view] : LOADING_LABEL);

  let pickCount = $state(0);
  const pick = (value: string | undefined) => {
    pickCount += 1;
    if (value === SAVE) onSaveAsOrgDefault();
    else if (value === "org-default" && view !== "org-default") onBackToOrgDefault();
    else if (value === "show-all" && view !== "show-all") onShowAll();
  };
</script>

<div class="w-56" data-testid="category-filter-bar" data-view={controls.settled ? view : undefined}>
  {#key pickCount}
    <Select.Root
      disabled={!controls.settled}
      selected={{ value: view, label: shownLabel }}
      onSelectedChange={(selected) => pick(selected?.value as string | undefined)}
    >
      <Select.Trigger name="category-view" aria-label="Category view">
        <span class="flex min-w-0 items-center gap-1">
          <span class="text-gray-500">View:</span>
          <Select.Value placeholder={shownLabel} />
        </span>
      </Select.Trigger>
      <Select.Content sameWidth={true}>
        <Select.Group>
          {#if hasDefault}
            <Select.Item value="org-default" label={VIEW_LABEL["org-default"]} isSelected={view === "org-default"}>
              {VIEW_LABEL["org-default"]}
            </Select.Item>
          {/if}
          <Select.Item value="show-all" label={VIEW_LABEL["show-all"]} isSelected={view === "show-all"}>
            {VIEW_LABEL["show-all"]}
          </Select.Item>
          {#if view === "custom"}
            <Select.Item value="custom" label={VIEW_LABEL.custom} isSelected={true}>
              {VIEW_LABEL.custom}
            </Select.Item>
          {/if}
          {#if canSave}
            <Select.Separator />
            <Select.Item
              value={SAVE}
              label="Save current as org default"
              isSelected={false}
              disabled={saveDisabled}
              data-testid="save-as-org-default"
              title={saveDisabled
                ? "Available once every document has loaded"
                : "Make the categories on screen this organisation's default"}
            >
              Save current as org default
            </Select.Item>
          {/if}
        </Select.Group>
      </Select.Content>
    </Select.Root>
  {/key}
</div>
